/**
 * Contact submission management interface for admins
 * Follows the unified UX pattern with stats, filtering, and modern design
 */

import { useState, useEffect, useMemo } from 'react';
import { adminApi } from '../../services/api';
import { ContactSubmission, PaginatedResponse, ContactStatus, ContactSubject } from '../../types';
import { FilterBar, FilterConfig } from '../shared/FilterBar';
import { Pagination } from '../shared/Pagination';
import { useAdminFilters } from './shared/hooks/useAdminFilters';
import { useSelection } from '../../hooks/useSelection';
import DataTable, { Column } from '../shared/DataTable';
import BulkActionToolbar, { BulkActionButton } from '../shared/BulkActionToolbar';
import { StatsWidget, StatItem } from '../common/StatsWidget';
import {
  Eye,
  Trash2,
  MessageSquare,
  Mail,
  CheckCircle,
  Clock,
  Archive,
  RefreshCw,
  HelpCircle,
  X,
  Save,
} from 'lucide-react';

// Filter configuration for contact submissions
const contactFilters: FilterConfig[] = [
  {
    type: 'search',
    label: 'Search',
    key: 'search',
    placeholder: 'Search name, email, or message...',
    width: 'lg',
  },
  {
    type: 'select',
    label: 'Status',
    key: 'status',
    width: 'md',
    options: [
      { value: '', label: 'All Statuses' },
      { value: 'new', label: 'New' },
      { value: 'read', label: 'Read' },
      { value: 'responded', label: 'Responded' },
      { value: 'closed', label: 'Closed' },
    ],
  },
  {
    type: 'select',
    label: 'Subject',
    key: 'subject',
    width: 'md',
    options: [
      { value: '', label: 'All Subjects' },
      { value: 'general', label: 'General' },
      { value: 'support', label: 'Support' },
      { value: 'billing', label: 'Billing' },
      { value: 'feedback', label: 'Feedback' },
      { value: 'other', label: 'Other' },
    ],
  },
  {
    type: 'date-range',
    label: 'Date',
    key: 'date',
    width: 'md',
  },
];

export default function ContactManagement(): JSX.Element {
  const [response, setResponse] = useState<PaginatedResponse<ContactSubmission> | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState<ContactSubmission | null>(null);
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // Selection state for bulk actions
  const { selectedIds, toggleSelection, toggleAll, clearSelection, selectedCount } = useSelection();

  // Use the admin filters hook with URL synchronization
  const { filters, debouncedFilters, setFilter, clearFilters } = useAdminFilters({
    page: 1,
    limit: 25,
    search: '',
    status: '',
    subject: '',
    dateFrom: '',
    dateTo: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  // Load submissions whenever debounced filters change
  useEffect(() => {
    loadSubmissions();
  }, [debouncedFilters]);

  const loadSubmissions = async () => {
    setLoading(true);
    try {
      // Build API params from filters
      const params: any = {
        page: debouncedFilters.page,
        limit: debouncedFilters.limit,
        sortBy: debouncedFilters.sortBy,
        sortOrder: debouncedFilters.sortOrder,
      };

      if (debouncedFilters.search) params.search = debouncedFilters.search;
      if (debouncedFilters.status) params.status = debouncedFilters.status;
      if (debouncedFilters.subject) params.subject = debouncedFilters.subject;
      if (debouncedFilters.dateFrom) params.dateFrom = debouncedFilters.dateFrom;
      if (debouncedFilters.dateTo) params.dateTo = debouncedFilters.dateTo;

      const data = await adminApi.listContacts(params);
      setResponse(data);
    } catch (error) {
      console.error('Failed to load contact submissions:', error);
      setResponse(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this contact submission?')) return;

    try {
      await adminApi.deleteContact(id);
      await loadSubmissions();
    } catch (error: any) {
      console.error('Failed to delete submission:', error);
      alert(error.message || 'Failed to delete submission');
    }
  };

  const handleViewDetails = (submission: ContactSubmission) => {
    setSelectedSubmission(submission);
  };

  const handleCloseDetails = () => {
    setSelectedSubmission(null);
  };

  const handleSort = (key: string) => {
    const newOrder = filters.sortBy === key && filters.sortOrder === 'asc' ? 'desc' : 'asc';
    setFilter('sortBy', key);
    setFilter('sortOrder', newOrder);
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (bulkProcessing) return;

    try {
      setBulkProcessing(true);
      let successCount = 0;
      let failedCount = 0;

      for (const id of selectedIds) {
        try {
          await adminApi.deleteContact(id);
          successCount++;
        } catch {
          failedCount++;
        }
      }

      alert(`Deleted: ${successCount}\nFailed: ${failedCount}`);
      clearSelection();
      await loadSubmissions();
    } catch (error) {
      console.error('Bulk delete error:', error);
      alert(`Failed to delete submissions: ${error}`);
    } finally {
      setBulkProcessing(false);
    }
  };

  // Helper function for status badge color
  const getStatusColor = (status: ContactStatus): string => {
    switch (status) {
      case 'new':
        return 'red';
      case 'read':
        return 'yellow';
      case 'responded':
        return 'green';
      case 'closed':
        return 'gray';
      default:
        return 'gray';
    }
  };

  // Helper function for subject badge color
  const getSubjectColor = (subject: ContactSubject): string => {
    switch (subject) {
      case 'general':
        return 'blue';
      case 'support':
        return 'purple';
      case 'billing':
        return 'green';
      case 'feedback':
        return 'amber';
      case 'other':
        return 'gray';
      default:
        return 'gray';
    }
  };

  // Table column definitions
  const columns: Column<ContactSubmission>[] = [
    {
      key: 'name',
      header: 'Contact',
      sortable: true,
      render: (submission) => (
        <div>
          <div className="font-medium text-sm">{submission.name}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">{submission.email}</div>
        </div>
      ),
    },
    {
      key: 'subject',
      header: 'Subject',
      sortable: true,
      render: (submission) => (
        <span
          className={`px-2 py-1 text-xs font-medium rounded capitalize bg-${getSubjectColor(submission.subject)}-100 text-${getSubjectColor(submission.subject)}-800 dark:bg-${getSubjectColor(submission.subject)}-900 dark:text-${getSubjectColor(submission.subject)}-200`}
        >
          {submission.subject}
        </span>
      ),
    },
    {
      key: 'message',
      header: 'Message',
      sortable: false,
      render: (submission) => (
        <span className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
          {submission.message.length > 80 ? `${submission.message.slice(0, 80)}...` : submission.message}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (submission) => (
        <span
          className={`px-2 py-1 text-xs font-medium rounded capitalize bg-${getStatusColor(submission.status)}-100 text-${getStatusColor(submission.status)}-800 dark:bg-${getStatusColor(submission.status)}-900 dark:text-${getStatusColor(submission.status)}-200`}
        >
          {submission.status}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Date',
      sortable: true,
      render: (submission) => (
        <div className="text-sm">
          {submission.createdAt && new Date(submission.createdAt).toLocaleDateString()}
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      sortable: false,
      render: (submission) => (
        <div className="flex gap-2">
          <button
            onClick={() => handleViewDetails(submission)}
            className="p-2 rounded bg-blue-600 hover:bg-blue-700 text-white transition-colors"
            title="View Details"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDelete(submission.id)}
            className="p-2 rounded bg-red-600 hover:bg-red-700 text-white transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  // Bulk action buttons
  const bulkActions: BulkActionButton[] = [
    {
      label: 'Delete Selected',
      onClick: handleBulkDelete,
      variant: 'danger',
      requireConfirmation: true,
      confirmMessage: `Delete ${selectedCount} contact submissions? This cannot be undone.`,
      disabled: bulkProcessing,
    },
  ];

  // These must be BEFORE any early return to satisfy React's rules of hooks
  const submissions = response?.items || [];
  const pagination = response?.pagination;

  // Calculate stats from current data
  const stats = useMemo(() => {
    const newCount = submissions.filter((s) => s.status === 'new').length;
    const respondedCount = submissions.filter((s) => s.status === 'responded').length;
    const closedCount = submissions.filter((s) => s.status === 'closed').length;

    return {
      total: pagination?.total || submissions.length,
      new: newCount,
      responded: respondedCount,
      closed: closedCount,
    };
  }, [submissions, pagination]);

  // Stat items for the widget
  const statItems: StatItem[] = useMemo(
    () => [
      {
        id: 'total',
        label: 'Total Messages',
        value: stats.total,
        icon: MessageSquare,
        variant: 'primary',
      },
      {
        id: 'new',
        label: 'New',
        value: stats.new,
        icon: Clock,
        variant: 'error',
      },
      {
        id: 'responded',
        label: 'Responded',
        value: stats.responded,
        icon: CheckCircle,
        variant: 'success',
      },
      {
        id: 'closed',
        label: 'Closed',
        value: stats.closed,
        icon: Archive,
        variant: 'default',
      },
    ],
    [stats]
  );

  // Early return AFTER all hooks have been called
  if (selectedSubmission) {
    return (
      <ContactDetails
        submission={selectedSubmission}
        onClose={handleCloseDetails}
        onDelete={handleDelete}
        onUpdate={loadSubmissions}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Contact Management</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            View and respond to customer contact submissions
          </p>
        </div>
        <button
          onClick={loadSubmissions}
          disabled={loading}
          className="btn btn-secondary inline-flex items-center gap-2"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Stats Section */}
      <section>
        <StatsWidget stats={statItems} columns={4} loading={loading} />
      </section>

      {/* Filters */}
      <FilterBar
        filters={contactFilters}
        values={filters}
        onChange={setFilter}
        onClear={clearFilters}
        resultsCount={pagination?.total}
      />

      {/* Contact Table */}
      <div className="card">
        <DataTable
          data={submissions}
          columns={columns}
          selectedIds={selectedIds}
          onSelectChange={toggleSelection}
          onSelectAll={toggleAll}
          onSort={handleSort}
          sortBy={filters.sortBy}
          sortOrder={filters.sortOrder as 'asc' | 'desc'}
          idKey="id"
          loading={loading}
          emptyMessage={submissions.length === 0 && !loading ? 'No contact submissions found' : ''}
        />
      </div>

      {/* Pagination */}
      {!loading && pagination && pagination.totalPages > 1 && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={(page) => setFilter('page', page)}
          onItemsPerPageChange={(limit) => setFilter('limit', limit)}
        />
      )}

      {/* Bulk Action Toolbar */}
      <BulkActionToolbar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={bulkActions}
      />

      {/* Help CTA */}
      <section className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-750 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white dark:bg-gray-700 rounded-full shadow-sm">
              <HelpCircle size={24} className="text-gray-600 dark:text-gray-400" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                Need help with contacts?
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Learn how to efficiently manage and respond to customer inquiries
              </p>
            </div>
          </div>
          <a href="/help" className="btn btn-secondary whitespace-nowrap">
            View Help Center
          </a>
        </div>
      </section>
    </div>
  );
}

/**
 * Contact details view with status update and admin notes
 */
interface ContactDetailsProps {
  submission: ContactSubmission;
  onClose: () => void;
  onDelete: (id: string) => void;
  onUpdate: () => void;
}

function ContactDetails({ submission, onClose, onDelete, onUpdate }: ContactDetailsProps): JSX.Element {
  const [status, setStatus] = useState<ContactStatus>(submission.status);
  const [adminNotes, setAdminNotes] = useState(submission.adminNotes || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updateContact(submission.id, { status, adminNotes });
      alert('Contact submission updated successfully');
      onUpdate();
      onClose();
    } catch (error: any) {
      console.error('Failed to update submission:', error);
      alert(error.message || 'Failed to update submission');
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (s: ContactStatus): string => {
    switch (s) {
      case 'new':
        return 'red';
      case 'read':
        return 'yellow';
      case 'responded':
        return 'green';
      case 'closed':
        return 'gray';
      default:
        return 'gray';
    }
  };

  const getSubjectColor = (subject: ContactSubject): string => {
    switch (subject) {
      case 'general':
        return 'blue';
      case 'support':
        return 'purple';
      case 'billing':
        return 'green';
      case 'feedback':
        return 'amber';
      case 'other':
        return 'gray';
      default:
        return 'gray';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Contact Details</h2>
        <button onClick={onClose} className="btn btn-secondary">
          ← Back to List
        </button>
      </div>

      {/* Contact Info */}
      <div className="card p-6 space-y-4">
        <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-4">
          <div>
            <h3 className="text-xl font-bold">{submission.name}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {new Date(submission.createdAt).toLocaleString()}
            </p>
          </div>
          <div className="flex gap-2">
            <span
              className={`px-3 py-1 text-sm font-medium rounded capitalize bg-${getSubjectColor(submission.subject)}-100 text-${getSubjectColor(submission.subject)}-800 dark:bg-${getSubjectColor(submission.subject)}-900 dark:text-${getSubjectColor(submission.subject)}-200`}
            >
              {submission.subject}
            </span>
            <span
              className={`px-3 py-1 text-sm font-medium rounded capitalize bg-${getStatusColor(submission.status)}-100 text-${getStatusColor(submission.status)}-800 dark:bg-${getStatusColor(submission.status)}-900 dark:text-${getStatusColor(submission.status)}-200`}
            >
              {submission.status}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Email</p>
            <a href={`mailto:${submission.email}`} className="font-medium text-primary-600 hover:underline flex items-center gap-1">
              <Mail size={14} />
              {submission.email}
            </a>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Submission ID</p>
            <p className="font-mono text-sm">{submission.id}</p>
          </div>
          {submission.respondedAt && (
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Responded At</p>
              <p className="font-medium text-sm">
                {new Date(submission.respondedAt).toLocaleString()}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Message */}
      <div className="card p-6">
        <h3 className="text-lg font-bold mb-4">Message</h3>
        <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg whitespace-pre-wrap">
          {submission.message}
        </div>
      </div>

      {/* Update Status & Admin Notes */}
      <div className="card p-6 space-y-4">
        <h3 className="text-lg font-bold">Update Submission</h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Status
          </label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as ContactStatus)}
            className="input w-full md:w-64"
          >
            <option value="new">New</option>
            <option value="read">Read</option>
            <option value="responded">Responded</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Admin Notes (Internal)
          </label>
          <textarea
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            placeholder="Add internal notes about this submission..."
            className="input w-full h-32 resize-none"
          />
        </div>

        <div className="flex gap-3 pt-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn btn-primary inline-flex items-center gap-2"
          >
            <Save size={16} />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button onClick={onClose} className="btn btn-secondary inline-flex items-center gap-2">
            <X size={16} />
            Cancel
          </button>
        </div>
      </div>

      {/* Reply via Email */}
      <div className="card p-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-700">
        <h3 className="font-bold text-blue-800 dark:text-blue-400 mb-2">Reply to Customer</h3>
        <p className="text-sm text-blue-800 dark:text-blue-400 mb-4">
          Click the button below to open your email client and compose a reply.
        </p>
        <a
          href={`mailto:${submission.email}?subject=Re: ${submission.subject === 'other' ? 'Your inquiry' : `Your ${submission.subject} inquiry`}`}
          className="btn bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-2"
        >
          <Mail size={16} />
          Compose Reply
        </a>
      </div>

      {/* Delete */}
      <div className="card p-6 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700">
        <h3 className="font-bold text-red-800 dark:text-red-400 mb-2">Delete Submission</h3>
        <p className="text-sm text-red-800 dark:text-red-400 mb-4">
          Permanently delete this contact submission. This action cannot be undone.
        </p>
        <button
          onClick={() => onDelete(submission.id)}
          className="btn bg-red-600 hover:bg-red-700 text-white inline-flex items-center gap-2"
        >
          <Trash2 size={16} />
          Delete Submission
        </button>
      </div>
    </div>
  );
}
